import { useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import TimePicker from '@/components/TimePicker';
import HoursMinutesField, {
  DURATION_TOO_LONG_HINT,
  durationFields,
  durationOutOfRange,
  minutesFromFields,
  type HoursMinutes,
} from '@/components/HoursMinutesField';
import { durationText } from '@/components/TimelineItem';

/**
 * Editing a fact in place, wherever the fact lives.
 *
 * There is no edit mode anywhere on the timeline and no Save button on it. A
 * fact is a button that becomes the field that produced it; blur or Enter
 * commits that one field, Escape puts it back, and one committed field is one
 * PATCH. A form's worth of writes for a planner who only changed the time is a
 * form, whatever it looks like.
 *
 * That grammar started on a block and is now also how a coverage window is
 * edited, which is what moved it out of `TimelineBlock` and into here. The
 * block kept what only a block knows — which of its four facts fit at its
 * height, and what a popover anchored to it should say. What came out is
 * everything that was never about blocks: three editors, the button that opens
 * one, the row that labels one, and the two hooks that make all of them behave
 * the same.
 *
 * The hooks are the part worth not rewriting. `useOneWayOut` is why a blur
 * arriving after a commit cannot write the same value twice, and `useAutoFocus`
 * is why a field opens focused without anything measuring the DOM — which is
 * also what keeps every one of these testable in a DOM with no layout engine.
 */

/**
 * A fact in the panel: a full-width target, not a word with a hit area.
 *
 * The complaint the panel answers is that four fields on sixty pixels of block
 * are four fields nobody can hit. Giving each one the width of the panel and a
 * surface that lights up under the pointer is most of the answer; the rest is
 * that it still says what it is until it is clicked, rather than pretending to
 * be an input that is not yet an input. `hover:bg-card/60` is the block's, and
 * it is invisible on a panel that is already card white.
 */
export const DETAIL_HIT =
  'rounded-lg px-1.5 py-1 text-sm hover:bg-[var(--color-surface-container-low)]';
/** The usual one: the width of the panel, bled back out to sit flush left. */
export const DETAIL_FIELD = `-mx-1.5 block w-full ${DETAIL_HIT}`;
/**
 * And the start of a range, which has the end of it beside it.
 *
 * Not `DETAIL_FIELD` with the right margin overridden: `-mx-1.5` and `mr-0`
 * are two rules for one property that `tailwind-merge` keeps both of, and
 * which of them wins is then a question about the order of a generated
 * stylesheet rather than about anything written here.
 */
export const DETAIL_FIELD_INLINE = `-ml-1.5 ${DETAIL_HIT}`;

/**
 * The same four editors, at the two sizes the two surfaces have room for.
 *
 * A block can be twelve pixels of plate, so its inputs are 28px tall with 12px
 * text in them — which is the complaint the panel exists to answer, and would
 * have been reproduced inside the panel by reusing the block's sizing there.
 * Class names only: the fields themselves, and the one commit path through
 * them, are shared.
 */
export const BLOCK_TEXT = 'h-7 min-w-0 flex-1 rounded-lg px-2 text-xs';
export const BLOCK_TIME = 'w-32 min-w-0';
export const PANEL_TEXT = 'h-9 w-full min-w-0 rounded-xl px-2.5 text-sm';
export const PANEL_TIME = 'w-full min-w-0';

/**
 * The lift a card gets when it is the one in play.
 *
 * One value, two reasons to wear it: a field open over the block, and a panel
 * open about it. Both mean the same thing — everything else on the axis is
 * context right now — and two shadows that drifted apart would say it twice in
 * two voices.
 */
export const LIFT = 'shadow-[0_8px_28px_rgba(49,51,47,0.16)]';
/**
 * A fact on the block, and the way into the field that produced it.
 *
 * A button rather than a click handler on the text, because this is the only
 * affordance the planner has that the value can be changed at all, and because
 * it is the keyboard's way in as well — the pencil it replaces was one tab stop
 * for four fields, which meant three of them were unreachable without it.
 *
 * Deliberately does *not* stop the pointerdown: the press has to reach the grab
 * surface above it, or the half of the block covered in text would stop being
 * draggable.
 */
export function FieldButton({
  name,
  onOpen,
  className,
  children,
}: {
  name: string;
  onOpen: () => void;
  className?: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      data-timeline-field
      aria-label={name}
      /* Its own tooltip, which is the point: the grab surface wraps these, and
         a native `title` on a parent answers for its children too — so without
         one here, hovering a label said "Drag to retime". True of the block
         underneath, and the wrong thing for the field on top of it to say. */
      title={name}
      onClick={onOpen}
      className={cn(
        'rounded text-left hover:bg-card/60 focus-visible:outline-1 focus-visible:outline-primary/70',
        className,
      )}
    >
      {children}
    </button>
  );
}

/**
 * One fact in the panel, under the name of what it is.
 *
 * The caps label goes while the row is a field. The editors name themselves —
 * `HoursMinutesField` carries a legend of its own — and a row reading "Length"
 * over "How long it runs" says one thing twice in a panel 288 pixels wide.
 */
export function DetailRow({
  label,
  open,
  children,
}: {
  label: string;
  /** This row is currently the field, not the fact. */
  open: boolean;
  children: ReactNode;
}) {
  return (
    <div>
      {!open && (
        <p className="label-caps mb-0.5 text-[0.625rem] text-muted-foreground">{label}</p>
      )}
      {children}
    </div>
  );
}

/** The one shape every in-place field has, so none of them can drift. */
export function useOneWayOut(onCommit: () => void, onCancel: () => void) {
  // A committed field unmounts, and a blur arriving after that would write the
  // same value a second time. One way out, taken once.
  const spent = useRef(false);
  return (commit: boolean) => {
    if (spent.current) return;
    spent.current = true;
    if (commit) onCommit();
    else onCancel();
  };
}

/** Focus what the planner just clicked, without measuring anything to do it. */
export function useAutoFocus<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  useEffect(() => {
    const el = ref.current;
    const input = el instanceof HTMLInputElement ? el : el?.querySelector('input');
    input?.focus();
    input?.select?.();
  }, []);
  return ref;
}

export function TextField({
  name,
  className,
  value,
  maxLength,
  placeholder,
  onCommit,
  onCancel,
}: {
  /** The accessible name — the block around it is the visible label. */
  name: string;
  /** How much room the surface it sits on has. See `BLOCK_TEXT`, `PANEL_TEXT`. */
  className: string;
  value: string;
  maxLength: number;
  placeholder?: string;
  onCommit: (value: string) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState(value);
  const ref = useAutoFocus<HTMLInputElement>();
  const leave = useOneWayOut(() => onCommit(draft), onCancel);

  return (
    <Input
      ref={ref}
      aria-label={name}
      className={className}
      maxLength={maxLength}
      placeholder={placeholder}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => leave(true)}
      onKeyDown={(e) => {
        // Only the keys this field actually answers for. An arrow key belongs
        // to the caret, and the block above already stands down while a field
        // is open — stopping it here as well is a second answer to one
        // question, and no test can fail while both are standing.
        if (e.key !== 'Enter' && e.key !== 'Escape') return;
        e.preventDefault();
        e.stopPropagation();
        leave(e.key === 'Enter');
      }}
    />
  );
}

/**
 * The time, with the same picker the forms use — and with its own two habits
 * accounted for rather than discovered later.
 *
 * `TimePicker` calls `onChange` on every keystroke that parses, so it is driven
 * from local state here and never straight into a PATCH; wire it the other way
 * and typing `6:45` files three writes. And its menu is open the whole time the
 * field has focus, which makes Escape ambiguous: the picker takes it to close
 * the menu. This takes it first, in the capture phase, because a planner who
 * presses Escape over a time they are half way through typing means "put it
 * back", and the menu closing on the way out is not a second decision.
 *
 * Enter is left to the picker while its menu has something highlighted — that
 * is the menu being chosen from, which is what Enter is for there — and commits
 * the field on the next one. The same rule the form this replaces used.
 */
export function TimeField({
  id,
  className,
  value,
  onCommit,
  onCancel,
}: {
  id: string;
  /** How much room the surface it sits on has. See `BLOCK_TIME`, `PANEL_TIME`. */
  className: string;
  value: string;
  onCommit: (clockTime: string) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState(value);
  const ref = useAutoFocus<HTMLDivElement>();
  const leave = useOneWayOut(() => onCommit(draft), onCancel);

  return (
    <div
      ref={ref}
      className={className}
      onKeyDownCapture={(e) => {
        if (e.key !== 'Escape') return;
        e.preventDefault();
        e.stopPropagation();
        leave(false);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !e.defaultPrevented) {
          e.preventDefault();
          e.stopPropagation();
          leave(true);
        }
      }}
      onBlur={(e) => {
        // A click on one of the menu's own options never gets here: the option
        // keeps the caret where it is. Anything that does is the planner
        // leaving the field.
        if (!e.currentTarget.contains(e.relatedTarget as Node)) leave(true);
      }}
    >
      <TimePicker id={id} label="Time" placeholder="7:30 PM" value={draft} onChange={setDraft} />
    </div>
  );
}

/**
 * How long it runs, in the grammar the rest of the app uses.
 *
 * Two fields rather than a minute count, and held as text: an emptied pair is
 * a legitimate answer — it means open-ended — and a typed `0` is a different
 * legitimate one. A single "45 min" box would be a fifth way of saying a span
 * of time in an app that has settled on one.
 */
export function DurationField({
  idPrefix,
  value,
  onCommit,
  onCancel,
}: {
  idPrefix: string;
  /** The stored field, where null is open-ended and 0 is a point in time. */
  value: number | null;
  onCommit: (minutes: number | null) => void;
  onCancel: () => void;
}) {
  const [pair, setPair] = useState<HoursMinutes>(durationFields(value));
  const ref = useAutoFocus<HTMLDivElement>();
  const minutes = minutesFromFields(pair);
  const invalid = durationOutOfRange(minutes);
  // Out of range is not a change to save. Enter holds the field open so it can
  // be fixed; walking away puts the old length back rather than sending a
  // number the server would refuse anyway.
  const leave = useOneWayOut(() => onCommit(minutes), onCancel);

  return (
    <div
      ref={ref}
      className="min-w-max"
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
          if (!invalid) leave(true);
        } else if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          leave(false);
        }
      }}
      onBlur={(e) => {
        // Tabbing from hours to minutes is not leaving the pair.
        if (e.currentTarget.contains(e.relatedTarget as Node)) return;
        leave(!invalid);
      }}
    >
      <HoursMinutesField
        idPrefix={idPrefix}
        legend="How long it runs"
        hoursLabel="Length in hours"
        minutesLabel="Length in minutes"
        value={pair}
        onChange={setPair}
        invalid={invalid}
        /* What the pair means, read back as it is typed, in the same words the
           block uses — and the only thing on screen that tells an emptied pair
           from a typed zero. */
        hint={invalid ? DURATION_TOO_LONG_HINT : durationText(minutes)}
      />
    </div>
  );
}